import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import classNames from '../../../utils/classNames';
import { ROUTES } from '../../../utils/routes';

interface DashboardWelcomeCardProps {
  canSeeAnalysis?: boolean;
  showStrategyButton?: boolean;
  onOpenStrategyModal?: () => void;
}

const ROLE_ITEMS = [
  'Assurer la passerelle entre le syndic et les services transaction / gestion locative',
  'Recueillir le consentement préalable des copropriétaires',
  'Coordonner les envois de communication RGPD conformes',
  'Suivre les actions planifiées sur 12 mois',
];

const MISSION_ITEMS = [
  'Être l\u2019interlocuteur identifié par les copropriétaires pour leurs projets immobiliers',
  'Générer des opportunités de mandat en restant dans le cadre légal',
  'Éviter tout détournement de finalité des données de copropriété',
  'Agir uniquement comme interface \u2014 jamais en démarchage direct',
];

function DashboardWelcomeCard({ canSeeAnalysis = false, showStrategyButton = false, onOpenStrategyModal }: DashboardWelcomeCardProps) {
  const [isFinaliteOpen, setIsFinaliteOpen] = useState(false);
  const [isReferentOpen, setIsReferentOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl shadow-md">
      {/* Hero gradient */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-rgpd-blue-dark to-rgpd-blue">
        <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/5" />
        <div className="flex flex-col gap-4 px-4 py-5 sm:px-6 md:px-10 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className="min-w-0">
            <div className="mb-1 text-[11px] font-bold tracking-wider text-white/60 uppercase">
              Tableau de bord
            </div>
            <h1 className="mb-2 text-lg font-extrabold text-white sm:text-[22px]">
              La stratégie CROISSANCE
            </h1>
            <p className="text-[13px] leading-relaxed text-white/75">
              Développez votre CA transaction, et gestion, en respectant le{' '}
              <strong className="text-white">RGPD</strong>.
            </p>
          </div>

          <div className="flex flex-col items-stretch gap-2.5 sm:shrink-0 sm:flex-row">
            {canSeeAnalysis && (
              <Link
                to={ROUTES.ANALYZING_MARKET_VISION}
                className="flex flex-col gap-1 rounded-xl border-[1.5px] border-green-300 bg-gradient-to-br from-green-50 to-green-100 px-4 py-3 shadow-sm transition-all hover:from-green-100 hover:to-green-200"
              >
                <div className="text-[10px] font-bold tracking-wider text-green-800 uppercase">
                  Analyse du parc · Les chiffres clés
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-green-800/85">
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
                    Vente sur le parc
                  </span>
                  <span className="hidden text-green-800/40 sm:inline">·</span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Potentiel location gestion
                  </span>
                </div>
              </Link>
            )}

            {showStrategyButton && onOpenStrategyModal && (
              <button
                type="button"
                onClick={onOpenStrategyModal}
                className="flex flex-col items-start gap-1 rounded-[10px] border-[1.5px] border-white/30 bg-white/10 px-4 py-2.5 text-left transition-colors hover:bg-white/20"
              >
                <div className="text-[9.5px] font-bold tracking-wider text-white/60 uppercase">
                  Documentation
                </div>
                <div className="flex items-center gap-1.5 text-xs font-extrabold text-white">
                  <span role="img" aria-label="clipboard">📋</span>
                  <span className="line-clamp-1">Présentation de la stratégie</span>
                  <span className="rounded-full bg-white/25 px-1.5 py-px text-[9px] font-extrabold">▶</span>
                </div>
                <div className="text-[10px] text-white/60">2 axes · 7 actions · 12 mois</div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Accordion bar */}
      <div className="border-y border-gray-200 bg-white">
        <div className="flex flex-col gap-0 px-4 sm:px-6 md:flex-row md:flex-wrap md:items-center md:px-10">
          {/* Segment 1: Détournement de finalité */}
          <button
            type="button"
            onClick={() => setIsFinaliteOpen((v) => !v)}
            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 py-3 transition-opacity hover:opacity-80"
          >
            <span className="text-[12.5px] font-bold text-slate-900">
              Pourquoi ce module ?
            </span>
            <span className="text-[12.5px] text-slate-900">
              Pour éviter le détournement de finalité
            </span>
            <span className="ml-1 shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10px] font-bold text-amber-800">
              c&apos;est quoi ? les risques ?
            </span>
            <ChevronDownIcon
              className={classNames(
                'h-2.5 w-2.5 shrink-0 text-gray-400 transition-transform duration-200',
                isFinaliteOpen && 'rotate-180',
              )}
            />
          </button>

          {/* Separator */}
          <div className="mx-5 hidden h-7 w-px shrink-0 bg-gray-200 md:block" />

          {/* Segment 2: Référent */}
          <button
            type="button"
            onClick={() => setIsReferentOpen((v) => !v)}
            className="flex flex-wrap items-center gap-2 border-t border-gray-100 py-3 transition-opacity hover:opacity-80 md:shrink-0 md:border-t-0"
          >
            <span className="text-[12.5px] font-bold text-slate-900">
              Le rôle du{' '}
              <strong className="text-rgpd-blue">&quot;Référent Mutation Location&quot;</strong>
            </span>
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-[10px] font-bold text-rgpd-blue-dark">
              Voir le rôle et la mission
            </span>
            <ChevronDownIcon
              className={classNames(
                'h-2.5 w-2.5 shrink-0 text-gray-400 transition-transform duration-200',
                isReferentOpen && 'rotate-180',
              )}
            />
          </button>
        </div>

        {/* Finalité accordion body */}
        {isFinaliteOpen && (
          <div className="border-t border-gray-100 px-4 pb-4 sm:px-6 md:px-10">
            <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4 text-[13px] leading-relaxed text-gray-900 shadow-sm">
              <strong className="mb-2 block text-sm text-rgpd-blue-dark">
                Le détournement de finalité, c&apos;est quoi ?
              </strong>
              Le fichier des copropriétaires, avec leurs données personnelles, doit servir
              exclusivement à gérer la copropriété. Utiliser ce fichier pour proposer des services
              de transaction ou de gestion locative revient à détourner cette finalité, ce qui est
              constitutif notamment d&apos;une infraction pénale passible de{' '}
              <strong>5 ans d&apos;emprisonnement et de 300 000 € d&apos;amende</strong>.
            </div>
          </div>
        )}

        {/* Référent accordion body */}
        {isReferentOpen && (
          <div className="border-t border-gray-100 px-4 pb-5 sm:px-6 md:px-10">
            <div className="mt-3 overflow-hidden rounded-[10px] border border-gray-200 bg-white">
              <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
                <p className="mb-2 text-[13px] font-bold text-slate-900">
                  Pourquoi désigner un référent mutation-location est-il nécessaire ?
                </p>
                <p className="text-[13px] leading-relaxed text-gray-600">
                  Ce rôle permet d&apos;assurer une passerelle claire entre le syndic, dans sa mission de
                  conseil, et les autres services (transaction, gestion locative), tout en respectant le
                  cadre légal. Le référent n&apos;a pas une démarche commerciale : il agit uniquement comme
                  interface. Il met en relation le copropriétaire avec les services concernés, uniquement
                  après avoir recueilli son consentement préalable lors de l&apos;échange. Ce fonctionnement
                  permet ainsi d&apos;éviter tout risque de détournement de finalité.
                </p>
              </div>

              <div className="flex flex-col gap-3 bg-gradient-to-r from-rgpd-blue-dark to-rgpd-blue px-4 py-3.5 sm:flex-row sm:items-center sm:px-5">
                <UserIcon className="hidden h-5 w-5 shrink-0 text-white sm:block" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-extrabold text-white">Référent Mutation Location</div>
                  <div className="text-[11px] text-white/75">
                    Interface entre la gestion de la copropriété et les services commerciaux du cabinet
                  </div>
                </div>
                <Link
                  to={ROUTES.COMMUNICATION_REFERENT_FORMATION}
                  className="flex w-full items-center justify-center gap-1.5 rounded-[7px] border border-white/30 bg-white/[.18] px-4 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-white/30 sm:w-auto"
                >
                  Rôle et missions complet
                  <ChevronRightIcon className="h-3 w-3" />
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2 sm:px-5">
                <div>
                  <div className="mb-2 text-[11px] font-extrabold tracking-wide text-green-600 uppercase">
                    <span role="img" aria-label="target">🎯</span> Son rôle
                  </div>
                  <ul className="flex flex-col gap-1 pl-4 text-[12.5px] leading-relaxed text-gray-700">
                    {ROLE_ITEMS.map((item) => (
                      <li key={item} className="list-disc">{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="mb-2 text-[11px] font-extrabold tracking-wide text-rgpd-blue uppercase">
                    <span role="img" aria-label="clipboard">📋</span> Sa mission
                  </div>
                  <ul className="flex flex-col gap-1 pl-4 text-[12.5px] leading-relaxed text-gray-700">
                    {MISSION_ITEMS.map((item) => (
                      <li key={item} className="list-disc">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="px-4 pb-4 sm:px-5">
                <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3.5 py-2.5 text-xs leading-relaxed text-orange-800">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                  <p>
                    <strong>Important :</strong> Le référent agit uniquement comme interface, après
                    recueil du consentement préalable — évitant tout risque de détournement de finalité
                    passible de <strong>5 ans d&apos;emprisonnement et 300 000 € d&apos;amende</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardWelcomeCard;
